import { RecentlyViewed } from '@theme/recently-viewed-products';

const PRODUCT_CARD_SECTION = 'recently-viewed-product-card';

class RecentlyViewedSection extends HTMLElement {
  /** @type {boolean} */
  #isLoading = false;

  connectedCallback() {
    if (this.dataset.recentlyViewedPerformed === 'true') {
      return;
    }

    const entries = this.#getEntriesToShow();
    if (entries.length === 0) {
      if (Shopify.designMode) return;
      this.remove();
      return;
    }

    this.#loadProducts(entries);
  }

  /**
   * @returns {{ id: string, handle: string | null }[]}
   */
  #getEntriesToShow() {
    const { productId, maxProducts } = this.dataset;
    const limit = Number(maxProducts) || 4;

    return RecentlyViewed.getEntries()
      .filter((entry) => String(entry.id) !== String(productId))
      .slice(0, limit);
  }

  /**
   * @param {{ id: string, handle: string | null }[]} entries
   */
  async #loadProducts(entries) {
    if (this.#isLoading) return;
    this.#isLoading = true;

    const grid = this.querySelector('[data-testid="recently-viewed-grid"]');
    if (!grid) {
      this.#isLoading = false;
      return;
    }

    try {
      const cards = await Promise.all(entries.map((entry) => this.#fetchEntryCard(entry)));
      const cardMarkup = cards.filter(Boolean).map((card) => card.outerHTML).join('');

      if (!cardMarkup) {
        this.remove();
        return;
      }

      grid.innerHTML = cardMarkup;
      this.dataset.recentlyViewedPerformed = 'true';
      grid.classList.add('is-loaded');
    } catch (error) {
      console.error('Recently viewed section error:', error);
      this.remove();
    } finally {
      this.#isLoading = false;
    }
  }

  /**
   * @param {{ id: string, handle: string | null }} entry
   * @returns {Promise<HTMLElement | null>}
   */
  async #fetchEntryCard(entry) {
    let { handle } = entry;

    if (!handle) {
      handle = await this.#resolveHandle(entry.id);
      if (handle) {
        RecentlyViewed.updateHandle(entry.id, handle);
      }
    }

    if (!handle) {
      return null;
    }

    const html = await this.#fetchProductCardByHandle(handle);
    return this.#parseCard(html);
  }

  /**
   * @param {string} productId
   * @returns {Promise<string | null>}
   */
  async #resolveHandle(productId) {
    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', `id:${productId}`);

    const response = await fetch(url);
    if (!response.ok) return null;

    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');

    for (const link of doc.querySelectorAll('a[href*="/products/"]')) {
      const card = link.closest('[data-product-id]');
      if (card?.getAttribute('data-product-id') === String(productId)) {
        const match = link.getAttribute('href')?.match(/\/products\/([^/?#]+)/);
        if (match?.[1]) return match[1];
      }
    }

    return null;
  }

  /**
   * @param {string} handle
   * @returns {Promise<string>}
   */
  async #fetchProductCardByHandle(handle) {
    const url = `/products/${handle}?section_id=${PRODUCT_CARD_SECTION}`;
    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`Failed to fetch product card for ${handle}`);
    }

    return response.text();
  }

  /**
   * @param {string} html
   * @returns {HTMLElement | null}
   */
  #parseCard(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const card = doc.querySelector('[data-recently-viewed-card]');

    if (card?.querySelector('product-card')) {
      return card;
    }

    return null;
  }
}

if (!customElements.get('recently-viewed-section')) {
  customElements.define('recently-viewed-section', RecentlyViewedSection);
}
